import type { Road } from '../types.js';
import { polylineLength } from '../geom.js';
import { KERB_MIN_ROAD_WIDTH, KERB_STONE_WIDTH, pavementWidthFor, ROAD_SPECS } from './classify.js';

/**
 * How wide the pavement is on each side of a street, and the footways that are that pavement
 * mapped a second time. DECISIONS.md §93.
 *
 * Side 0 is the left of the way's own direction of travel and side 1 the right — the same
 * convention `ribbon` in the renderer uses, where side 0 is the edge at the tangent turned a
 * quarter towards -z. For a point `p` and a segment `a → b`, `cross(b - a, p - a)` is negative
 * on side 0.
 */

/**
 * Widest a pavement is drawn when a mapped sidewalk says it reaches that far: the pavement
 * then runs from the kerb to the outer edge of the mapped way, and the way itself is dropped.
 * Past this the strip between them is a verge, a car park or a front garden rather than a
 * pavement, and the sidewalk is left as a path of its own.
 */
export const PAVEMENT_ABSORB_MAX = 6;
/**
 * How far beyond the kerb line the centreline of a mapped sidewalk may run and still be this
 * street's pavement. Most of Leszno's 149 `footway=sidewalk` ways sit 0–6 m off the nearest
 * carriageway's edge; the median is 2.9 m.
 */
const SIDEWALK_REACH = 6;
/** Half the width a mapped sidewalk is taken to have. OSM rarely says. */
const SIDEWALK_HALF_WIDTH = 1;
/** Directions within 25° of each other are running alongside. A path leaving the street is not. */
const ALONGSIDE_COS = 0.9;
/** Share of a side's length the mapped sidewalks have to cover before they set its width. */
const SIDE_COVERAGE = 0.4;

/** The paved strip outboard of the kerb stone on each side, in metres; 0 where there is none. */
export function pavementSides(road: Road): readonly [number, number] {
  if (!ROAD_SPECS[road.cls].drivable || road.width < KERB_MIN_ROAD_WIDTH || road.roundabout) return NONE;
  if (road.pavement) return road.pavement;
  const w = pavementWidthFor(road.width);
  return [w, w];
}
const NONE = [0, 0] as const;

/** Distance from the centreline to the back of the pavement on each side; 0 for no kerb. */
export function pavementReach(road: Road): readonly [number, number] {
  const [left, right] = pavementSides(road);
  const half = road.width / 2 + KERB_STONE_WIDTH;
  return [left > 0 ? half + left : 0, right > 0 ? half + right : 0];
}

interface Segment {
  road: Road;
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Unit direction. */
  ux: number;
  uz: number;
  len: number;
}

const CELL = 32;

class SegmentGrid {
  readonly segments: Segment[] = [];
  private readonly grid = new Map<number, number[]>();

  constructor(roads: Road[], pad: (road: Road) => number) {
    for (const road of roads) {
      const reach = pad(road);
      for (let k = 2; k < road.pts.length; k += 2) {
        const ax = road.pts[k - 2];
        const az = road.pts[k - 1];
        const bx = road.pts[k];
        const bz = road.pts[k + 1];
        const len = Math.hypot(bx - ax, bz - az);
        if (len < 1e-6) continue;
        const index = this.segments.length;
        this.segments.push({ road, ax, az, bx, bz, ux: (bx - ax) / len, uz: (bz - az) / len, len });
        for (let cx = Math.floor((Math.min(ax, bx) - reach) / CELL); cx <= Math.floor((Math.max(ax, bx) + reach) / CELL); cx++) {
          for (let cz = Math.floor((Math.min(az, bz) - reach) / CELL); cz <= Math.floor((Math.max(az, bz) + reach) / CELL); cz++) {
            const key = cx * 100003 + cz;
            const bucket = this.grid.get(key);
            if (bucket) bucket.push(index);
            else this.grid.set(key, [index]);
          }
        }
      }
    }
  }

  near(x: number, z: number): number[] {
    return this.grid.get(Math.floor(x / CELL) * 100003 + Math.floor(z / CELL)) ?? [];
  }
}

/** Distance from a point to a segment, and which side of it the point is on. */
function measure(s: Segment, x: number, z: number): { d: number; side: 0 | 1 } {
  let t = (x - s.ax) * s.ux + (z - s.az) * s.uz;
  t = t < 0 ? 0 : t > s.len ? s.len : t;
  const dx = x - (s.ax + s.ux * t);
  const dz = z - (s.az + s.uz * t);
  const cross = s.ux * (z - s.az) - s.uz * (x - s.ax);
  return { d: Math.hypot(dx, dz), side: cross < 0 ? 0 : 1 };
}

/** Even samples along a polyline: midpoint, length represented, and the local direction. */
function* samples(pts: number[], step: number): Generator<{ x: number; z: number; w: number; ux: number; uz: number }> {
  for (let k = 2; k < pts.length; k += 2) {
    const ax = pts[k - 2];
    const az = pts[k - 1];
    const bx = pts[k];
    const bz = pts[k + 1];
    const length = Math.hypot(bx - ax, bz - az);
    if (length < 1e-6) continue;
    const n = Math.max(1, Math.ceil(length / step));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      yield { x: ax + (bx - ax) * t, z: az + (bz - az) * t, w: length / n, ux: (bx - ax) / length, uz: (bz - az) / length };
    }
  }
}

/**
 * Gives each street the pavement its mapped sidewalks say it has, and removes the footways
 * and cycleways that merely retrace a street the generator already draws.
 *
 * OSM maps a pavement two ways. In most of Kraków it is an attribute of the carriageway
 * (`sidewalk=both`), which costs nothing here because the renderer draws a pavement beside
 * every street wide enough for a kerb. In Paris, in Leszno — and increasingly everywhere — it
 * is a separate `highway=footway` way running parallel a few metres off the centreline, plus a
 * `footway=crossing` at every junction and a `highway=cycleway` for every painted bike lane.
 * Those are the same square metres of city, mapped twice.
 *
 * §55 dropped the second copy when it lay inside the pavement the renderer draws anyway, and
 * left it as a 2.6 m tan path when it did not. In Leszno that was 55 of the 149 mapped
 * sidewalks dropped and 94 kept, so one street had a grey 1.4 m pavement, the next the same
 * pavement and a tan ribbon a metre beyond it with grass in between, and the one after that
 * a tan ribbon half underneath the pavement. Which one a street got depended on how far from
 * the centreline somebody had happened to draw the footway.
 *
 * So first, a mapped sidewalk *widens* its street's pavement on its side: a footway with no
 * name, running alongside a kerbed street within `SIDEWALK_REACH` of its kerb line, is that
 * street's pavement, and the pavement on that side then reaches its outer edge. A side is only
 * widened where the sidewalks along it cover a good share of its length, and to their median
 * edge, so one stray metre of path cannot widen a whole street. Then everything non-drivable
 * that lies mostly inside a street's pavement, as now drawn, is dropped as before. What survives
 * is what is genuinely the only thing there: a pedestrianised street, a park path, an alley, a
 * towpath, a sidewalk set well back behind a verge.
 *
 * The drop threshold is deliberately above half. A path that clips a junction and carries on
 * across a square is a real path; a pavement is inside its street for its whole length, and a
 * crossing is inside for all of the short one it has.
 *
 * `sidewalkLike` says which of the non-drivable ways may set a pavement's width — unnamed
 * footways, paths and cycleways, not steps or a named pedestrian street.
 */
const PATH_ENVELOPE_MARGIN = 1;
const PATH_SAMPLE_STEP = 4;
const PATH_DROP_FRACTION = 0.65;

export function absorbSidewalks(roads: Road[], sidewalkLike: (road: Road) => boolean): void {
  const streets = roads.filter((r) => ROAD_SPECS[r.cls].drivable);
  const paths = roads.filter((r) => !ROAD_SPECS[r.cls].drivable);
  if (!streets.length || !paths.length) return;

  // --- 1. what the mapped sidewalks say about each side --------------------------------------
  // Every street, not only the kerbed ones: a footway nearer a service road than the avenue
  // behind it belongs to the service road, which has no pavement to widen.
  const kerbGrid = new SegmentGrid(streets, (r) => r.width / 2 + KERB_STONE_WIDTH + SIDEWALK_REACH + SIDEWALK_HALF_WIDTH);
  /** Per street and side: [outer edge, length] votes. */
  const votes = new Map<Road, [number, number][][]>();
  for (const path of paths) {
    if (!sidewalkLike(path)) continue;
    let total = 0;
    let along = 0;
    const mine: { road: Road; side: 0 | 1; edge: number; w: number }[] = [];
    for (const p of samples(path.pts, PATH_SAMPLE_STEP)) {
      total += p.w;
      let best: { s: Segment; d: number; side: 0 | 1 } | null = null;
      for (const index of kerbGrid.near(p.x, p.z)) {
        const s = kerbGrid.segments[index];
        const m = measure(s, p.x, p.z);
        if (!best || m.d < best.d) best = { s, d: m.d, side: m.side };
      }
      if (!best || pavementSides(best.s.road)[best.side] <= 0) continue;
      const kerbLine = best.s.road.width / 2;
      const beyond = best.d - kerbLine;
      if (beyond > SIDEWALK_REACH) continue;
      if (Math.abs(p.ux * best.s.ux + p.uz * best.s.uz) < ALONGSIDE_COS) continue;
      along += p.w;
      // Out past the stone to the far edge of the footway, and never narrower than the default.
      const edge = Math.max(0, beyond) + SIDEWALK_HALF_WIDTH - KERB_STONE_WIDTH;
      mine.push({ road: best.s.road, side: best.side, edge, w: p.w });
    }
    // A path that only brushes a street on its way somewhere else says nothing about it.
    if (total <= 0 || along / total < PATH_DROP_FRACTION) continue;
    for (const v of mine) {
      let sides = votes.get(v.road);
      if (!sides) votes.set(v.road, (sides = [[], []]));
      sides[v.side].push([v.edge, v.w]);
    }
  }
  for (const [road, sides] of votes) {
    const length = polylineLength(road.pts);
    const base = pavementSides(road);
    const out: [number, number] = [base[0], base[1]];
    for (const side of [0, 1] as const) {
      const list = sides[side];
      const covered = list.reduce((a, [, w]) => a + w, 0);
      if (covered < SIDE_COVERAGE * length) continue;
      const edge = weightedMedian(list);
      out[side] = Math.min(PAVEMENT_ABSORB_MAX, Math.max(base[side], Math.round(edge * 100) / 100));
    }
    if (out[0] !== base[0] || out[1] !== base[1]) road.pavement = out;
  }

  // --- 2. drop what now lies inside a street ---------------------------------------------------
  const MAX_PAD = KERB_STONE_WIDTH + PAVEMENT_ABSORB_MAX + PATH_ENVELOPE_MARGIN;
  const grid = new SegmentGrid(streets, (r) => r.width / 2 + MAX_PAD);
  const covered = (x: number, z: number): boolean => {
    for (const index of grid.near(x, z)) {
      const s = grid.segments[index];
      const m = measure(s, x, z);
      const reach = pavementReach(s.road)[m.side];
      const pad = (reach > 0 ? reach : s.road.width / 2) + PATH_ENVELOPE_MARGIN;
      if (m.d <= pad) return true;
    }
    return false;
  };

  const doomed = new Set<Road>();
  for (const path of paths) {
    let inside = 0;
    let total = 0;
    // By length rather than by vertex: OSM puts a node at every kerb and none along a
    // straight, so counting vertices would weigh a junction as heavily as the block after it.
    for (const p of samples(path.pts, PATH_SAMPLE_STEP)) {
      total += p.w;
      if (covered(p.x, p.z)) inside += p.w;
    }
    if (total > 0 && inside / total >= PATH_DROP_FRACTION) doomed.add(path);
  }
  if (!doomed.size) return;

  // In place, because `roads` is the array the world is built from and every index into it
  // downstream — junctions, the graph, the stats — is taken after this runs.
  let write = 0;
  for (let read = 0; read < roads.length; read++) {
    if (doomed.has(roads[read])) continue;
    roads[write++] = roads[read];
  }
  roads.length = write;
}

function weightedMedian(list: [number, number][]): number {
  const sorted = [...list].sort((a, b) => a[0] - b[0]);
  const half = sorted.reduce((a, [, w]) => a + w, 0) / 2;
  let run = 0;
  for (const [v, w] of sorted) {
    run += w;
    if (run >= half) return v;
  }
  return sorted[sorted.length - 1][0];
}
